import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader, CheckCircle } from 'lucide-react';
import Navbar from '@/components/Navbar';
import TextToSpeechButton from '@/components/TextToSpeechButton';
import VoiceRecorder from '@/components/VoiceRecorder';
import { api } from '@/utils/api';
import { useLanguage } from '@/LanguageContext';

const Screening = ({ user, onLogout }) => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [child, setChild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [assessmentType, setAssessmentType] = useState('developmental_milestones');
  const [responses, setResponses] = useState({});
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchChild();
  }, [childId]);

  const fetchChild = async () => {
    try {
      const res = await api.getChild(childId);
      setChild(res.data);
    } catch (error) {
      console.error('Error fetching child:', error);
    } finally {
      setLoading(false);
    }
  };

  // Translated questions based on language
  const getAssessmentQuestions = () => ({
    developmental_milestones: [
      { id: 'motor_skills', question: t('screeningQuestions.motorSkills'), type: 'yes_no' },
      { id: 'crawling', question: t('screeningQuestions.crawling'), type: 'yes_no' },
      { id: 'standing', question: t('screeningQuestions.standing'), type: 'yes_no' },
      { id: 'speech', question: t('screeningQuestions.speech'), type: 'yes_no' },
      { id: 'eye_contact', question: t('screeningQuestions.eyeContact'), type: 'yes_no' },
      { id: 'social_smile', question: t('screeningQuestions.socialSmile'), type: 'yes_no' },
      { id: 'grasp', question: t('screeningQuestions.grasp'), type: 'yes_no' },
      { id: 'recognition', question: t('screeningQuestions.recognition'), type: 'yes_no' }
    ],
    cognitive_assessment: [
      { id: 'problem_solving', question: t('screeningQuestions.problemSolving'), type: 'yes_no' },
      { id: 'object_permanence', question: t('screeningQuestions.objectPermanence'), type: 'yes_no' },
      { id: 'cause_effect', question: t('screeningQuestions.causeEffect'), type: 'yes_no' },
      { id: 'attention_span', question: t('screeningQuestions.attentionSpan'), type: 'yes_no' },
      { id: 'imitation', question: t('screeningQuestions.imitation'), type: 'yes_no' }
    ],
    social_emotional: [
      { id: 'separation_anxiety', question: t('screeningQuestions.separationAnxiety'), type: 'yes_no' },
      { id: 'stranger_awareness', question: t('screeningQuestions.strangerAwareness'), type: 'yes_no' },
      { id: 'play_interaction', question: t('screeningQuestions.playInteraction'), type: 'yes_no' },
      { id: 'emotional_expression', question: t('screeningQuestions.emotionalExpression'), type: 'yes_no' },
      { id: 'comfort_seeking', question: t('screeningQuestions.comfortSeeking'), type: 'yes_no' }
    ]
  });

  const handleResponseChange = (questionId, value) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const screeningData = {
        child_id: childId,
        assessment_type: assessmentType,
        age_months: child.age_months,
        responses: responses
      };

      await api.createScreening(screeningData);
      setSuccess(true);
      setTimeout(() => {
        navigate(`/child/${childId}`);
      }, 2000);
    } catch (error) {
      console.error('Error creating screening:', error);
      alert('Failed to submit screening. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl font-bold text-[#0F4C81]">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96" data-testid="success-message">
          <div className="text-center">
            <CheckCircle className="mx-auto text-[#10B981] mb-4" size={64} />
            <h2 className="text-2xl font-bold text-[#111827] mb-2">{t('screening.successMessage')}</h2>
            <p className="text-[#4B5563]">{t('screening.redirecting')}</p>
          </div>
        </div>
      </div>
    );
  }

  const assessmentQuestions = getAssessmentQuestions();
  const questions = assessmentQuestions[assessmentType] || [];

  return (
    <>
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="screening-page">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate(`/child/${childId}`)}
          data-testid="back-button"
          className="flex items-center space-x-2 text-[#0F4C81] font-semibold mb-6 hover:underline"
        >
          <ArrowLeft size={20} />
          <span>{t('common.back')}</span>
        </button>

        <div className="bg-white rounded-2xl border-2 border-slate-100 p-8 shadow-sm">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-[#111827] mb-2 font-['Nunito']" data-testid="screening-title">
              {t('screening.title')}
            </h1>
            <p className="text-lg text-[#4B5563]">
              {t('screening.screeningFor')}: <span className="font-bold">{child?.name}</span> ({child?.age_months} {t('screening.months')})
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Assessment Type Selection */}
            <div className="mb-8">
              <label className="block text-lg font-bold text-[#111827] mb-4">{t('screening.assessmentType')}</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    assessmentType === 'developmental_milestones'
                      ? 'border-[#0F4C81] bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="radio"
                    data-testid="assessment-developmental"
                    name="assessment_type"
                    value="developmental_milestones"
                    checked={assessmentType === 'developmental_milestones'}
                    onChange={(e) => {
                      setAssessmentType(e.target.value);
                      setResponses({});
                    }}
                    className="sr-only"
                  />
                  <span className="font-bold text-[#111827]">{t('screening.developmental')}</span>
                </label>

                <label
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    assessmentType === 'cognitive_assessment'
                      ? 'border-[#0F4C81] bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="radio"
                    data-testid="assessment-cognitive"
                    name="assessment_type"
                    value="cognitive_assessment"
                    checked={assessmentType === 'cognitive_assessment'}
                    onChange={(e) => {
                      setAssessmentType(e.target.value);
                      setResponses({});
                    }}
                    className="sr-only"
                  />
                  <span className="font-bold text-[#111827]">{t('screening.cognitive')}</span>
                </label>

                <label
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    assessmentType === 'social_emotional'
                      ? 'border-[#0F4C81] bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="radio"
                    data-testid="assessment-social"
                    name="assessment_type"
                    value="social_emotional"
                    checked={assessmentType === 'social_emotional'}
                    onChange={(e) => {
                      setAssessmentType(e.target.value);
                      setResponses({});
                    }}
                    className="sr-only"
                  />
                  <span className="font-bold text-[#111827]">{t('screening.socialEmotional')}</span>
                </label>
              </div>
            </div>

            {/* Questions */}
            <div className="space-y-6 mb-8">
              <h2 className="text-2xl font-bold text-[#111827] mb-4 font-['Nunito']">{t('screening.questions')}</h2>
              {questions.map((question, index) => (
                <div
                  key={question.id}
                  data-testid={`question-${question.id}`}
                  className="p-6 bg-[#F3F4F6] rounded-xl relative z-10"
                >
                  <div className="flex items-start justify-between mb-4">
                    <p className="text-lg font-semibold text-[#111827] flex-1">
                      {index + 1}. {question.question}
                    </p>
                    <TextToSpeechButton 
                      text={question.question} 
                      language={language || 'en'} 
                      size={20}
                      className="ml-4 flex-shrink-0"
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="flex items-center space-x-2 cursor-pointer" onClick={() => handleResponseChange(question.id, 'yes')}>
                      <input
                        type="radio"
                        data-testid={`${question.id}-yes`}
                        name={question.id}
                        value="yes"
                        checked={responses[question.id] === 'yes'}
                        onChange={(e) => handleResponseChange(question.id, e.target.value)}
                        className="w-5 h-5 text-[#0F4C81] pointer-events-none"
                        required
                      />
                      <span className="font-semibold text-[#111827] pointer-events-none">{t('common.yes')}</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer" onClick={() => handleResponseChange(question.id, 'no')}>
                      <input
                        type="radio"
                        data-testid={`${question.id}-no`}
                        name={question.id}
                        value="no"
                        checked={responses[question.id] === 'no'}
                        onChange={(e) => handleResponseChange(question.id, e.target.value)}
                        className="w-5 h-5 text-[#0F4C81] pointer-events-none"
                        required
                      />
                      <span className="font-semibold text-[#111827] pointer-events-none">{t('common.no')}</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer" onClick={() => handleResponseChange(question.id, 'unsure')}>
                      <input
                        type="radio"
                        data-testid={`${question.id}-unsure`}
                        name={question.id}
                        value="unsure"
                        checked={responses[question.id] === 'unsure'}
                        onChange={(e) => handleResponseChange(question.id, e.target.value)}
                        className="w-5 h-5 text-[#0F4C81] pointer-events-none"
                        required
                      />
                      <span className="font-semibold text-[#111827] pointer-events-none">{t('common.unsure')}</span>
                    </label>
                    <div className="flex-shrink-0">
                      <VoiceRecorder
                        language={language || 'en'}
                        onTranscription={(text) => {
                          const lowerText = text.toLowerCase();
                          if (lowerText.includes('yes') || lowerText.includes('हाँ') || lowerText.includes('ஆம்') || lowerText.includes('ಹೌದು')) {
                            handleResponseChange(question.id, 'yes');
                          } else if (lowerText.includes('no') || lowerText.includes('नहीं') || lowerText.includes('இல்லை') || lowerText.includes('ಇಲ್ಲ')) {
                            handleResponseChange(question.id, 'no');
                          } else {
                            handleResponseChange(question.id, 'unsure');
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              data-testid="submit-screening-button"
              disabled={submitting || Object.keys(responses).length < questions.length}
              className="w-full rounded-xl py-4 text-xl font-bold uppercase tracking-wide shadow-md bg-[#0F4C81] text-white hover:bg-[#0d3d6b] transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <span className="flex items-center justify-center space-x-2">
                  <Loader className="animate-spin" size={24} />
                  <span>{t('screening.analyzing')}</span>
                </span>
              ) : (
                t('screening.submitScreening')
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
    
    {/* Copyright Footer */}
    <footer className="bg-[#F3F4F6] border-t border-gray-200 py-3 text-center">
      <p className="text-sm text-[#6B7280]">© 2025 Hamrith Consulting Services LLP (HCS). All Rights Reserved.</p>
    </footer>
    </>
  );
};

export default Screening;
